import { useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </span>
);

export const ReviewItem = ({ review: r }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid={`review-item-${r.id}`}>
    <div className="flex items-center justify-between mb-2">
      <p className="font-heading font-semibold text-slate-900">{r.name}</p>
      <Stars value={r.rating} />
    </div>
    <p className="text-sm text-slate-600">{r.comment}</p>
    <p className="text-xs text-slate-400 mt-3">
      {new Date(r.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
    </p>
  </div>
);

const EMPTY = { name: "", rating: 0, comment: "" };

export const ReviewForm = ({ onPublished }) => {
  const [form, setForm] = useState(EMPTY);
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      toast.error("Selecciona una valoración de 1 a 5 estrellas");
      return;
    }
    setSending(true);
    try {
      await api.post("/reviews", form);
      toast.success("¡Gracias por tu reseña!");
      setForm(EMPTY);
      onPublished?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-5 sm:p-7 h-fit space-y-5" data-testid="review-form">
      <h3 className="font-heading font-semibold text-lg text-slate-900 flex items-center gap-2">
        <MessageSquarePlus className="h-5 w-5 text-cyan-500" /> Deja tu reseña
      </h3>
      <div className="space-y-2">
        <Label>Tu valoración</Label>
        <div className="flex gap-1" data-testid="review-stars-input">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" data-testid={`review-star-${n}`}
              onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
              onClick={() => setForm({ ...form, rating: n })} className="p-0.5">
              <Star className={`h-8 w-8 transition-colors ${n <= (hover || form.rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="review-name">Nombre</Label>
        <Input id="review-name" data-testid="review-name-input" required minLength={2}
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tu nombre" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="review-comment">Comentario</Label>
        <Textarea id="review-comment" data-testid="review-comment-input" required minLength={5} rows={4}
          value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
          placeholder="Cuéntanos tu experiencia..." />
      </div>
      <Button type="submit" disabled={sending} data-testid="review-submit-btn"
        className="bg-cyan-500 hover:bg-cyan-600 rounded-full px-8 h-11 w-full sm:w-auto">
        {sending ? "Enviando..." : "Publicar Reseña"}
      </Button>
    </form>
  );
};
